import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import KNAdvancedDropdown from 'components/KNAdvancedDropdown/KNAdvancedDropdown'
import KNDatePickerNext from 'components/KNDatePicker/KNDatePickerNext'
import { useTripsView } from 'context/TripsViewContext'
import { addDays, endOfDay, startOfDay, subDays } from 'date-fns'
import { analyticsEvent } from 'global/helpers/analytics'
import { type ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { BranchesData } from './TripDashboard.types'

interface TripListFiltersProps {
  branches?: BranchesData
}

const TripListFilters = ({ branches }: TripListFiltersProps): ReactElement => {
  const { t } = useTranslation()
  const { filters, filtersDirty, setFilters, resetFilters } = useTripsView()

  const handleResetClick = useCallback((): void => {
    resetFilters()
    analyticsEvent('polestar_cs_reset_trip_filters')
  }, [])

  return (
    <Box sx={{ position: 'relative' }}>
      <Grid data-guide="trip-filters" container spacing={2}>
        <Grid size={{ xs: 12, sm: 2 }}>
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={filters.keywords}
            onChange={(event, newValue): void => {
              setFilters({
                keywords: [...newValue] as string[],
              })
              if (newValue.length > 0) {
                analyticsEvent('polestar_cs_trip_filters', ['keywords', newValue.join(', ')])
              }
            }}
            renderValue={(tagValue, getTagProps): ReactElement[] =>
              tagValue.map((option, index) => (
                // eslint-disable-next-line react/jsx-key
                <Chip {...getTagProps({ index })} variant="outlined" size="small" label={option} />
              ))
            }
            renderInput={(params): ReactElement => (
              <TextField {...params} label={t('screens.cs.trip_dashboard.filters.search')} />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <KNAdvancedDropdown
            label={t('screens.cs.trip_dashboard.filters.status')}
            options={[
              { value: 'NEW', label: t('screens.cs.trip_dashboard.statuses.new') },
              { value: 'TRIP_ASSIGNED', label: t('screens.cs.trip_dashboard.statuses.assigned') },
              { value: 'IN_PROGRESS', label: t('screens.cs.trip_dashboard.statuses.in_progress') },
              { value: 'DELIVERED', label: t('screens.cs.trip_dashboard.statuses.delivered') },
              { value: 'COMPLETED', label: t('screens.cs.trip_dashboard.statuses.completed') },
            ]}
            value={filters.status}
            onChange={(newValue): void => {
              setFilters({
                status: [...newValue].sort((a, b) => a.localeCompare(b)),
              })
            }}
            onDelete={(value) => {
              setFilters({
                status: filters.status.filter((status) => status !== value),
              })
            }}
            multiple
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <KNDatePickerNext
            mode="range"
            value={{ from: filters.fromDate, to: filters.toDate }}
            minDate={startOfDay(subDays(new Date(), 30))}
            maxDate={endOfDay(addDays(new Date(), 60))}
            onChange={(value): void => {
              setFilters({
                fromDate: value?.from,
                toDate: value?.to,
              })
            }}
            labelFrom={t('screens.cs.trip_dashboard.filters.date_from')}
            labelTo={t('screens.cs.trip_dashboard.filters.date_to')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 2 }}>
          <KNAdvancedDropdown
            label={t('screens.cs.trip_dashboard.filters.branches')}
            options={
              branches
                ? Object.entries(branches).map((branch) => ({
                  value: branch[0],
                  label: branch[1],
                }))
                : []
            }
            value={filters.branches}
            onChange={(newValue): void => {
              setFilters({
                branches: newValue,
              })
            }}
            onDelete={(value) => {
              setFilters({
                branches: filters.branches.filter((branch) => branch !== value),
              })
            }}
            multiple
          />
        </Grid>
      </Grid>
      {filtersDirty && (
        <Button
          size="small"
          variant="text"
          color="primary"
          onClick={handleResetClick}
          sx={{
            padding: 0,
            minHeight: 'auto',
          }}
        >
          {t('general.reset_filters')}
        </Button>
      )}
    </Box>
  )
}

export default TripListFilters
